import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { fr, enUS, ar } from 'date-fns/locale';
import {
    X, FileText, Download, Printer, DollarSign, Calendar,
    XCircle, Send, Car, User, AlertTriangle, Info
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { quoteService } from "../../services/quotesService";
import { inspectionService } from "../../services/inspectionService";
import { API_URL } from '../../config/api.config';
import { useLanguage } from '../../hooks/useLanguage';

interface QuoteItem {
    reference: string;
    description: string;
    quantity: number;
    unitPriceHT: number;
    tvaRate: number;
    discountPercent: number;
    totalHT: number;
    totalTTC: number;
}

interface QuoteReference {
    quoteId: string;
    garageId: string;
    status: string;
    totalTTC: number;
    createdAt: Date;
}

interface InspectionDetails {
    _id: string;
    vehicleBrand: string;
    vehicleModel: string;
    vehicleYear: string;
    clientName: string;
    clientPhone: string;
    status: string;
}

interface Quote {
    _id: string;
    quoteNumber: string;
    inspectionId: string;
    garageId: string;
    status: 'draft' | 'sent' | 'accepted' | 'rejected' | 'cancelled';
    items: QuoteItem[];
    totalHT: number;
    totalTVA: number;
    totalDiscount: number;
    totalTTC: number;
    notes?: string;
    validUntil: Date;
    createdAt: Date;
    updatedAt: Date;
    inspectionDetails?: InspectionDetails;
}

interface QuoteDetailsModalProps {
    quoteId: string;
    onClose: () => void;
    onInspectionStatusChange?: (id: string, status: string) => void;
}

export const QuoteDetailsModal: React.FC<QuoteDetailsModalProps> = ({
                                                                        quoteId, onClose, onInspectionStatusChange
                                                                    }) => {
    const { t, currentLanguage, isRTL } = useLanguage();
    const [quote, setQuote] = useState<Quote | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [statusUpdateLoading, setStatusUpdateLoading] = useState<boolean>(false);
    const [otherAcceptedQuote, setOtherAcceptedQuote] = useState<boolean>(false);

    const getDateLocale = () => {
        switch (currentLanguage) {
            case 'en': return enUS;
            case 'ar': return ar;
            default: return fr;
        }
    };

    useEffect(() => {
        const fetchQuoteDetails = async () => {
            try {
                setLoading(true);
                const response = await quoteService.getQuoteDetails(quoteId);
                setQuote(response);
                if (response.inspectionId) {
                    const inspection = await inspectionService.getInspectionById(response.inspectionId);
                    const hasOtherAcceptedQuote = inspection.quotes &&
                        inspection.quotes.some((q: QuoteReference) => q.status === 'accepted' && q.quoteId !== quoteId);
                    setOtherAcceptedQuote(hasOtherAcceptedQuote);
                }
                setError(null);
            } catch (err: any) {
                console.error('Error fetching quote details:', err);
                setError(err.response?.data?.message || t('quoteDetails.errorMessage'));
            } finally {
                setLoading(false);
            }
        };
        fetchQuoteDetails();
    }, [quoteId, t]);

    const handleDownload = async () => {
        try {
            window.open(`${API_URL}/quotes/${quoteId}/download`, '_blank');
        } catch (err) {
            console.error('Error downloading quote:', err);
            toast.error(t('quoteDetails.toasts.downloadError'));
        }
    };

    const handleUpdateStatus = async (newStatus: 'cancelled' | 'sent') => {
        try {
            setStatusUpdateLoading(true);
            await quoteService.updateQuoteStatus(quoteId, newStatus);
            setQuote(prev => prev ? { ...prev, status: newStatus } : null);
            if (newStatus === 'cancelled') toast.success(t('quoteDetails.toasts.cancelled'));
            else if (newStatus === 'sent') toast.success(t('quoteDetails.toasts.sent'));
        } catch (err: any) {
            console.error(`Error updating quote status to ${newStatus}:`, err);
            toast.error(err.response?.data?.message || t('quoteDetails.toasts.statusUpdateError'));
        } finally {
            setStatusUpdateLoading(false);
        }
    };

    const getStatusLabel = (status: string) => t(`quoteDetails.status.${status}`, status);

    const getStatusColor = (status: string) => {
        const statusColors: { [key: string]: string } = {
            draft: 'bg-gray-100 text-gray-800',
            cancelled: 'bg-gray-100 text-gray-800',
            sent: 'bg-blue-100 text-blue-800',
            accepted: 'bg-green-100 text-green-800',
            rejected: 'bg-red-100 text-red-800'
        };
        return statusColors[status] || 'bg-gray-100 text-gray-800';
    };

    const canEditQuote = quote && (quote.status === 'draft' || quote.status === 'sent');
    const isInspectionCompleted = quote?.inspectionDetails?.status === 'completed';

    if (loading) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                <div className={`bg-white p-8 rounded-lg shadow-xl w-full max-w-2xl ${isRTL ? 'rtl' : 'ltr'}`}>
                    <div className="flex justify-center items-center h-40">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !quote) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                <div className={`bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl ${isRTL ? 'rtl' : 'ltr'}`}>
                    <div className={`flex justify-between items-center mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <h2 className="text-xl font-semibold text-gray-800">{t('quoteDetails.error')}</h2>
                        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><X className="w-5 h-5" /></button>
                    </div>
                    <p className="text-red-600">{error || t('quoteDetails.errorMessage')}</p>
                    <div className={`mt-6 flex ${isRTL ? 'justify-start' : 'justify-end'}`}>
                        <button onClick={onClose} className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700">
                            {t('quoteDetails.close')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black bg-opacity-50">
            {/* Full-screen on mobile, constrained modal on sm+ */}
            <div className={`
                bg-white w-full h-full overflow-y-auto
                sm:rounded-lg sm:h-auto sm:max-h-[90vh] sm:max-w-4xl sm:m-4
                ${isRTL ? 'rtl' : 'ltr'}
            `}>
                {/* Header */}
                <div className="flex justify-between items-start sm:items-center p-4 sm:p-6 border-b sticky top-0 bg-white z-10" dir={isRTL ? 'rtl' : 'ltr'}>
                    <div className="flex items-start sm:items-center gap-3">
                        <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600 flex-shrink-0 mt-0.5 sm:mt-0" />
                        <div>
                            <h2 className="text-base sm:text-xl font-semibold text-gray-800">
                                {t('quoteDetails.title')} #{quote.quoteNumber}
                            </h2>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                                <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(quote.status)}`}>
                                    {getStatusLabel(quote.status)}
                                </span>
                                <span className="text-xs text-gray-500">
                                    {t('quoteDetails.createdOn')} {format(new Date(quote.createdAt), 'PPP', { locale: getDateLocale() })}
                                </span>
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full flex-shrink-0">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                    {/* Alert other accepted quote */}
                    {otherAcceptedQuote && (
                        <div className={`bg-yellow-50 border border-yellow-200 p-3 rounded-lg flex items-start gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <h3 className="font-medium text-yellow-700 text-sm">{t('quoteDetails.alerts.otherAccepted.title')}</h3>
                                <p className="text-xs text-yellow-600">{t('quoteDetails.alerts.otherAccepted.description')}</p>
                            </div>
                        </div>
                    )}

                    {/* Vehicle & client — stack on mobile */}
                    {quote.inspectionDetails && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                                <div className={`flex items-center gap-2 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                    <Car className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                                    <h3 className="font-medium text-sm sm:text-base">{t('quoteDetails.sections.vehicleInfo.title')}</h3>
                                </div>
                                <p className="text-sm">
                                    <span className="font-medium">{quote.inspectionDetails.vehicleBrand} {quote.inspectionDetails.vehicleModel}</span>
                                    {' '}({quote.inspectionDetails.vehicleYear})
                                </p>
                                <p className="mt-1 text-xs text-gray-500">
                                    {t('quoteDetails.sections.vehicleInfo.inspectionStatus')} <span className="font-medium">{quote.inspectionDetails.status}</span>
                                </p>
                            </div>
                            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                                <div className={`flex items-center gap-2 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                    <User className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                                    <h3 className="font-medium text-sm sm:text-base">{t('quoteDetails.sections.client.title')}</h3>
                                </div>
                                <p className="text-sm">{quote.inspectionDetails.clientName}</p>
                                {quote.inspectionDetails.clientPhone && (
                                    <p className="text-sm text-gray-600 mt-1">{quote.inspectionDetails.clientPhone}</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* General info + summary — stack on mobile */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div className="p-3 sm:p-4 bg-gray-50 rounded-lg" dir={isRTL ? 'rtl' : 'ltr'}>
                            <h3 className={`font-medium text-gray-700 mb-2 text-sm sm:text-base ${isRTL ? 'text-right' : 'text-left'}`}>
                                {t('quoteDetails.sections.generalInfo.title')}
                            </h3>
                            <div className="space-y-2">
                                <div className="flex items-start gap-2">
                                    <Calendar className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="text-xs text-gray-500">{t('quoteDetails.sections.generalInfo.validUntil')}</p>
                                        <p className="font-medium text-sm">{format(new Date(quote.validUntil), 'PPP', { locale: getDateLocale() })}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-2">
                                    <DollarSign className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="text-xs text-gray-500">{t('quoteDetails.sections.generalInfo.totalAmount')}</p>
                                        <p className="font-medium text-base sm:text-lg text-green-600">{quote.totalTTC.toFixed(2)} {t('quoteDetails.currency')}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="p-3 sm:p-4 bg-gray-50 rounded-lg" dir={isRTL ? 'rtl' : 'ltr'}>
                            <h3 className={`font-medium text-gray-700 mb-2 text-sm sm:text-base ${isRTL ? 'text-right' : 'text-left'}`}>
                                {t('quoteDetails.sections.summary.title')}
                            </h3>
                            <div className="space-y-1 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-500">{t('quoteDetails.sections.summary.totalHT')}</span>
                                    <span className="font-medium">{quote.totalHT.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">{t('quoteDetails.sections.summary.vat')}</span>
                                    <span className="font-medium">{quote.totalTVA.toFixed(2)} {t('quoteDetails.currency')}</span>
                                </div>
                                {quote.totalDiscount > 0 && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">{t('quoteDetails.sections.summary.discount')}</span>
                                        <span className="font-medium text-red-600">-{quote.totalDiscount.toFixed(2)} {t('quoteDetails.currency')}</span>
                                    </div>
                                )}
                                <div className="border-t pt-1 mt-2">
                                    <div className="flex justify-between font-bold">
                                        <span>{t('quoteDetails.sections.summary.totalTTC')}</span>
                                        <span>{quote.totalTTC.toFixed(2)} {t('quoteDetails.currency')}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Items table — horizontal scroll on mobile */}
                    <div>
                        <h3 className="font-medium text-gray-700 mb-3 text-sm sm:text-base">{t('quoteDetails.sections.services.title')}</h3>
                        <div className="overflow-x-auto -mx-4 sm:mx-0">
                            <div className="inline-block min-w-full px-4 sm:px-0">
                                <table className={`min-w-full bg-white rounded-lg border text-sm ${isRTL ? 'rtl' : ''}`}>
                                    <thead className="bg-gray-50">
                                    <tr>
                                        {[
                                            t('quoteDetails.sections.services.columns.ref'),
                                            t('quoteDetails.sections.services.columns.description'),
                                            t('quoteDetails.sections.services.columns.qty'),
                                            t('quoteDetails.sections.services.columns.unitPrice'),
                                            t('quoteDetails.sections.services.columns.vat'),
                                            t('quoteDetails.sections.services.columns.discount'),
                                            t('quoteDetails.sections.services.columns.total'),
                                        ].map((label, i) => (
                                            <th key={i} className={`py-2 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider border-b ${i < 2 ? (isRTL ? 'text-right' : 'text-left') : 'text-right'}`}>
                                                {label}
                                            </th>
                                        ))}
                                    </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                    {quote.items.map((item, index) => (
                                        <tr key={index} className={index % 2 === 0 ? '' : 'bg-gray-50'}>
                                            <td className={`py-2 px-3 text-xs text-gray-500 whitespace-nowrap ${isRTL ? 'text-right' : ''}`}>{item.reference}</td>
                                            <td className={`py-2 px-3 text-sm font-medium ${isRTL ? 'text-right' : ''}`}>{item.description}</td>
                                            <td className="py-2 px-3 text-sm text-right">{item.quantity}</td>
                                            <td className="py-2 px-3 text-sm text-right whitespace-nowrap">{item.unitPriceHT.toFixed(2)} {t('quoteDetails.currency')}</td>
                                            <td className="py-2 px-3 text-sm text-right">{item.tvaRate}%</td>
                                            <td className="py-2 px-3 text-sm text-right">{item.discountPercent > 0 ? `${item.discountPercent}%` : '-'}</td>
                                            <td className="py-2 px-3 text-sm text-right font-medium whitespace-nowrap">{item.totalTTC.toFixed(2)} {t('quoteDetails.currency')}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Notes */}
                    {quote.notes && (
                        <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                            <h3 className="font-medium text-gray-700 mb-2 text-sm sm:text-base">{t('quoteDetails.sections.notes.title')}</h3>
                            <p className="text-sm text-gray-600 whitespace-pre-line">{quote.notes}</p>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="border-t pt-4 flex flex-col sm:flex-row sm:justify-between gap-3" dir={isRTL ? 'rtl' : 'ltr'}>
                        <div className="flex flex-wrap gap-2">
                            <button onClick={handleDownload} className="inline-flex items-center gap-2 px-3 py-2 sm:px-4 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm">
                                <Download className="w-4 h-4" />
                                {t('quoteDetails.actions.download')}
                            </button>
                            <button onClick={() => window.print()} className="inline-flex items-center gap-2 px-3 py-2 sm:px-4 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm">
                                <Printer className="w-4 h-4" />
                                {t('quoteDetails.actions.print')}
                            </button>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            {quote.status === 'draft' && !isInspectionCompleted && (
                                <button onClick={() => handleUpdateStatus('sent')} disabled={statusUpdateLoading} className="inline-flex items-center gap-2 px-3 py-2 sm:px-4 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-sm">
                                    <Send className="w-4 h-4" />
                                    {t('quoteDetails.actions.sendToClient')}
                                </button>
                            )}
                            {(quote.status === 'sent' || quote.status === 'draft') && !isInspectionCompleted && (
                                <button onClick={() => handleUpdateStatus('cancelled')} disabled={statusUpdateLoading} className="inline-flex items-center gap-2 px-3 py-2 sm:px-4 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 text-sm">
                                    <XCircle className="w-4 h-4" />
                                    {t('quoteDetails.actions.cancelQuote')}
                                </button>
                            )}
                            {quote.status === 'accepted' && (
                                <div className="bg-green-50 p-2 sm:p-3 rounded-lg text-green-700 flex items-center gap-2 text-sm">
                                    <Info className="w-4 h-4" />
                                    <span>{t('quoteDetails.statusMessages.accepted')}</span>
                                </div>
                            )}
                            {quote.status === 'rejected' && (
                                <div className="bg-red-50 p-2 sm:p-3 rounded-lg text-red-700 flex items-center gap-2 text-sm">
                                    <Info className="w-4 h-4" />
                                    <span>{t('quoteDetails.statusMessages.rejected')}</span>
                                </div>
                            )}
                            {quote.status === 'cancelled' && (
                                <div className="bg-gray-50 p-2 sm:p-3 rounded-lg text-gray-700 flex items-center gap-2 text-sm">
                                    <Info className="w-4 h-4" />
                                    <span>{t('quoteDetails.statusMessages.cancelled')}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};